import { Link } from "react-router-dom";
import { Seo } from "@/components/site/Seo";
import { LegalHero, LegalBody, LegalCta, P, UL, Note } from "@/components/site/Legal";
import { IMAGES, CONTACT } from "@/lib/data";

const LAST_UPDATED = "11 August 2026";

const SECTIONS = [
  {
    id: "agreement",
    title: "Agreement to These Terms",
    body: (
      <>
        <P>
          These Terms &amp; Conditions govern your use of {CONTACT.website} and the advertising services
          provided by Twins Media, an outdoor and railway station advertising business operating from Manipal,
          Udupi, Karnataka, India ("Twins Media", "we", "us" or "our").
        </P>
        <P>
          By browsing this website, submitting an enquiry, or booking an advertising campaign with us, you agree
          to these Terms. If you do not accept them, please do not use this website or engage our services.
        </P>
        <P>
          Where a signed contract, work order or release order exists between you and Twins Media for a specific
          campaign, that document will prevail over these Terms to the extent of any conflict.
        </P>
      </>
    ),
  },
  {
    id: "definitions",
    title: "Definitions",
    body: (
      <>
        <UL
          items={[
            "\"Client\" means any person, business or agency that enquires about, books or purchases advertising services from Twins Media.",
            "\"Campaign\" means the advertising activity booked by the Client, including its display period, locations and formats.",
            "\"Display Site\" means the railway station panel, digital screen, hoarding, transit surface or other location on which the advertisement is displayed.",
            "\"Creative\" means the artwork, copy, logos, images, video and other material supplied by the Client for display.",
            "\"Authority\" means Indian Railways, railway divisions, municipal corporations, local bodies, licensors or any other body whose approval is required for a Display Site.",
          ]}
        />
      </>
    ),
  },
  {
    id: "website-use",
    title: "Use of This Website",
    body: (
      <>
        <P>
          This website is provided for information about our advertising services and to allow you to contact
          us. You agree to use it lawfully and not to:
        </P>
        <UL
          items={[
            "Attempt to gain unauthorised access to the website, its servers or any connected system.",
            "Introduce viruses, malicious code or any material designed to disrupt the website.",
            "Copy, reproduce, scrape or republish website content for commercial purposes without our written permission.",
            "Submit false, misleading or abusive information through our enquiry forms.",
            "Use the website in a way that infringes the rights of others or breaches applicable Indian law.",
          ]}
        />
        <P>
          We may suspend, withdraw or restrict access to all or part of the website at any time without notice,
          for maintenance or for any operational reason.
        </P>
      </>
    ),
  },
  {
    id: "enquiries-quotations",
    title: "Enquiries and Quotations",
    body: (
      <>
        <P>
          Information on this website about services, locations, formats and coverage is indicative and for
          general guidance. It does not constitute a binding offer.
        </P>
        <UL
          items={[
            "Rates, availability and site details are confirmed only in a written quotation issued by Twins Media.",
            "Unless stated otherwise, a quotation remains valid for 15 days from its date of issue.",
            "Quotations are subject to the Display Site remaining available and to the required Authority approvals at the time of confirmation.",
            "Submitting an enquiry through this website does not reserve a Display Site or create a booking.",
          ]}
        />
      </>
    ),
  },
  {
    id: "booking",
    title: "Booking and Confirmation",
    body: (
      <>
        <P>
          A booking is confirmed only when Twins Media accepts a written order from the Client and receives the
          agreed advance payment.
        </P>
        <UL
          items={[
            "All Display Sites are offered on a first-confirmed basis and remain subject to availability until booking is confirmed.",
            "Availability and continued use of Display Sites depend on permissions, licences and contracts held with the relevant Authority.",
            "Where a specific Display Site becomes unavailable before or during a Campaign for reasons outside our control, we will offer an alternative site of comparable value, extend the display period, or refund the unutilised amount for that site.",
            "Campaign start dates are estimates and may shift due to permission timelines, printing schedules, site handover or weather conditions.",
          ]}
        />
      </>
    ),
  },
  {
    id: "payment",
    title: "Payment Terms",
    body: (
      <>
        <UL
          items={[
            "Unless agreed otherwise in writing, an advance payment is required before production and installation begin, with the balance payable as per the agreed schedule.",
            "All rates are exclusive of GST and other applicable taxes, levies and statutory charges, which are payable by the Client at prevailing rates.",
            "Unless expressly included in the quotation, printing, flex or vinyl production, mounting, transportation and permission or licence fees are charged separately.",
            "Invoices are payable within the period stated on the invoice. Delayed payments may attract interest and may result in suspension of the Campaign.",
            "Twins Media reserves the right to withhold installation, pause a display or remove material where payment is overdue, without liability for loss of display days.",
          ]}
        />
        <Note>
          Payments should be made only to bank accounts confirmed on an official Twins Media invoice. Please
          verify account details with us by phone before transferring funds.
        </Note>
      </>
    ),
  },
  {
    id: "creative",
    title: "Creative Material and Artwork",
    body: (
      <>
        <P>
          The Client is responsible for supplying print-ready Creative that meets the specifications and
          deadlines we share for each format.
        </P>
        <UL
          items={[
            "Creative must be received by the agreed deadline. Late delivery may delay installation and does not extend the Campaign period or reduce the amount payable.",
            "The Client is responsible for checking and approving proofs. Twins Media is not liable for spelling, contact details, pricing or factual errors present in approved Creative.",
            "Where Twins Media assists with design, the Client must approve the final version in writing before production.",
            "Reprints or re-installations required due to changes requested by the Client after approval will be chargeable.",
            "Colour reproduction on print and digital media may vary slightly from on-screen proofs due to material, lighting and ambient conditions.",
          ]}
        />
      </>
    ),
  },
  {
    id: "content-standards",
    title: "Advertising Content Standards",
    body: (
      <>
        <P>
          The Client warrants that all Creative supplied for display complies with applicable law and
          advertising standards in India.
        </P>
        <UL
          items={[
            "Creative must comply with the Advertising Standards Council of India (ASCI) Code, the Consumer Protection Act, 2019 and rules on misleading advertisements, and all other applicable laws and Authority guidelines.",
            "Creative must not be obscene, defamatory, discriminatory, politically inflammatory, misleading, or infringe any third-party trademark, copyright or other right.",
            "The Client is solely responsible for the accuracy, claims, offers and disclaimers contained in the Creative.",
            "Twins Media may refuse, withdraw or require modification of any Creative that, in its reasonable opinion or on Authority instruction, breaches these standards — without refund where the breach is attributable to the Client.",
          ]}
        />
      </>
    ),
  },
  {
    id: "display",
    title: "Display, Installation and Maintenance",
    body: (
      <>
        <UL
          items={[
            "Twins Media will make reasonable efforts to install and maintain the Campaign in good condition for the agreed display period.",
            "Display Sites are outdoor and public locations. Normal wear, weathering, dust, minor damage and occasional obstruction by third parties can occur and do not, by themselves, constitute a service failure.",
            "Where material is significantly damaged during the display period due to reasons within our control, we will repair or replace it within a reasonable time.",
            "Digital display slots run on a rotation shared with other advertisers unless exclusivity is expressly purchased.",
            "Monitoring photographs, where provided, are indicative of display status on the date they were captured.",
          ]}
        />
      </>
    ),
  },
  {
    id: "cancellation",
    title: "Cancellation and Rescheduling",
    body: (
      <>
        <UL
          items={[
            "Cancellation requests must be made in writing and take effect from the date we acknowledge them.",
            "Amounts already incurred towards printing, production, permissions, transportation and installation are non-refundable.",
            "Advance payments made to secure a Display Site may be forfeited where the site has been blocked and other bookings declined on the Client's behalf.",
            "Rescheduling of a confirmed Campaign is subject to site availability and may attract additional charges.",
            "Twins Media may terminate a Campaign where the Client breaches these Terms, fails to make payment, or supplies Creative that violates content standards.",
          ]}
        />
      </>
    ),
  },
  {
    id: "force-majeure",
    title: "Circumstances Beyond Our Control",
    body: (
      <>
        <P>
          Twins Media is not liable for delay, interruption or non-performance caused by events beyond its
          reasonable control, including:
        </P>
        <UL
          items={[
            "Withdrawal, suspension or non-renewal of permissions by Indian Railways, municipal bodies or any other Authority.",
            "Government orders, restrictions, station renovation, redevelopment or infrastructure works.",
            "Storms, heavy rain, floods, natural calamities, fire, accident, vandalism or theft.",
            "Strikes, protests, civil disturbance, power failure or network outage.",
            "Any other event of force majeure.",
          ]}
        />
        <P>
          In such cases we will make reasonable efforts to offer an alternative site, extend the display period,
          or refund the proportionate value of the unutilised display period, at our discretion in discussion
          with the Client.
        </P>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    body: (
      <>
        <P>
          All content on this website — including the Twins Media name, logo, the "Defining Your Brands" and
          "The Railway Advertiser" identities, text, layout, graphics and photographs — is owned by or licensed
          to Twins Media and is protected under applicable intellectual property laws.
        </P>
        <UL
          items={[
            "You may not reproduce, distribute or create derivative works from our website content without prior written permission.",
            "The Client retains ownership of its own trademarks, logos and Creative.",
            "By supplying Creative, the Client grants Twins Media a limited licence to reproduce, print, install and display that material for the purpose of executing the Campaign.",
            "Designs originated by Twins Media remain our property unless assigned to the Client in writing.",
          ]}
        />
      </>
    ),
  },
  {
    id: "portfolio",
    title: "Campaign Photographs and References",
    body: (
      <>
        <P>
          Twins Media may photograph installed campaigns and use those photographs, along with the Client's name
          and brand identity, in our portfolio, website, presentations and marketing material as a reference of
          work executed.
        </P>
        <P>
          If you would prefer that your campaign is not used in this way, simply write to us at {CONTACT.email}{" "}
          and we will exclude it.
        </P>
      </>
    ),
  },
  {
    id: "disclaimer",
    title: "Disclaimers",
    body: (
      <>
        <P>
          Advertising increases visibility; it cannot guarantee commercial outcomes. Twins Media makes no
          representation or warranty regarding sales, enquiries, footfall conversion, brand recall or any other
          business result arising from a Campaign.
        </P>
        <UL
          items={[
            "Footfall, reach and impression figures shared by us are estimates based on available data and are indicative only.",
            "Website content is provided on an \"as is\" basis, without warranty of any kind, and may be updated without notice.",
            "We do not warrant that the website will be uninterrupted, error-free or free of harmful components.",
          ]}
        />
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    body: (
      <>
        <P>
          To the maximum extent permitted by law, Twins Media shall not be liable for any indirect, incidental,
          special or consequential loss, including loss of profit, business, goodwill, revenue or anticipated
          savings, arising out of or in connection with a Campaign or the use of this website.
        </P>
        <P>
          Our total aggregate liability in respect of any claim relating to a Campaign shall not exceed the
          amount actually paid by the Client to Twins Media for that specific Campaign.
        </P>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnity",
    body: (
      <>
        <P>
          The Client agrees to indemnify and hold harmless Twins Media, its owners, employees and partners
          against all claims, damages, penalties, costs and legal expenses arising from:
        </P>
        <UL
          items={[
            "The content of the Creative supplied by the Client, including claims of infringement, defamation, misleading advertisement or statutory violation.",
            "Any action initiated by an Authority, consumer body, regulator or third party in relation to the Client's advertisement.",
            "Breach by the Client of these Terms or of applicable law.",
          ]}
        />
      </>
    ),
  },
  {
    id: "third-party-links",
    title: "Third-Party Links and Services",
    body: (
      <>
        <P>
          This website contains links and embedded services from third parties, including Google Maps and
          WhatsApp. These are provided for convenience only. Twins Media does not control and is not responsible
          for the content, availability or practices of any third-party service, and their inclusion does not
          imply endorsement.
        </P>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Privacy",
    body: (
      <>
        <P>
          Your use of this website and our services is also governed by our{" "}
          <Link to="/privacy-policy" className="text-maroon underline underline-offset-4 hover:text-gold transition-colors">
            Privacy Policy
          </Link>
          , which explains how we collect, use, share and protect personal information. Please read it alongside
          these Terms.
        </P>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    body: (
      <>
        <P>
          We may revise these Terms &amp; Conditions from time to time to reflect changes in our services,
          operations or applicable law. The current version will always be published on this page with an
          updated date.
        </P>
        <P>
          Continued use of the website or our services after any revision constitutes acceptance of the updated
          Terms. Campaigns already confirmed will continue to be governed by the Terms in force at the time of
          confirmation.
        </P>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law and Jurisdiction",
    body: (
      <>
        <P>
          These Terms &amp; Conditions are governed by and construed in accordance with the laws of India.
        </P>
        <P>
          Any dispute arising out of or in connection with these Terms, this website or a Campaign shall be
          subject to the exclusive jurisdiction of the competent courts at Udupi, Karnataka, India. The parties
          will first attempt to resolve any dispute amicably through discussion before initiating proceedings.
        </P>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    body: (
      <>
        <P>For any clarification regarding these Terms &amp; Conditions, please contact us:</P>
        <div className="bg-paper border border-charcoal/10 p-6 md:p-8 space-y-3">
          <p className="font-display text-2xl text-charcoal">Twins Media</p>
          <address className="not-italic text-charcoal-muted leading-relaxed">
            {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
          </address>
          <p className="text-charcoal-muted">
            Phone — <a href="tel:+919980549123" className="text-maroon hover:text-gold transition-colors">{CONTACT.phone}</a>
          </p>
          <p className="text-charcoal-muted break-all">
            Email — <a href={`mailto:${CONTACT.email}`} className="text-maroon hover:text-gold transition-colors">{CONTACT.email}</a>
          </p>
          <p className="text-charcoal-muted">Business hours — Monday to Saturday, 9:00 AM to 6:00 PM IST</p>
        </div>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <Seo
        title="Terms & Conditions"
        description="Terms and conditions governing the use of the Twins Media website and our railway station, digital display, hoarding and transit advertising services across India."
        path="/terms"
      />
      <LegalHero
        overline="Legal"
        title={["Terms &", <span key="s" className="italic text-gold">Conditions</span>]}
        subtitle="The terms on which we provide railway station, digital display, hoarding and transit advertising services — and the basis on which you use this website."
        updated={LAST_UPDATED}
        image={IMAGES.outdoorHoarding}
      />
      <LegalBody sections={SECTIONS} />
      <LegalCta heading="Need clarity before you book? Let's talk it through." cta="Contact Us" />
    </div>
  );
}
